// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import PageHeader from "../../../components/PageHeader";
import PageTitle from "../../../components/PageTitle";
import { useTranslation } from "react-i18next";
import PageBody from "../../../components/PageBody";
import { Button, Divider, Table } from "antd";
import { SpinIcon } from "../../../components/SpinIcon";
import { columns } from "./MissingUserListColumns";
import {
  missingUserExportUrl,
  requestGetMissingUsers
} from "../../../../state/missingUser/MissingUsersEffects";
import { displayError } from "../../../../utils/displayError";
import { DownloadOutlined } from "@ant-design/icons";
import { downloadFile } from "../../../../utils/FileUtils";

export interface MissingUser {
  email: string;
  attemptNumber: number;
  lastAttempt: Date;
}

const MissingUserListsScreen: React.FunctionComponent<RouteComponentProps> = () => {
  const { t } = useTranslation();

  const [dataHasLoaded, setDataHasLoaded] = useState<boolean>(false);

  const [missingUsers, setMissingUsers] = useState<MissingUser[]>([]);

  useEffect(() => {
    requestGetMissingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return requestGetMissingUsers.done.watch(({ result }) => {
      setDataHasLoaded(true);
      if (result.ok && result.data) {
        setMissingUsers(
          result.data.map(missingUser => ({
            email: missingUser.email,
            attemptNumber: missingUser.attemptNumber,
            lastAttempt: new Date(missingUser.lastAttempt)
          }))
        );
      } else {
        displayError("missing_users.load_failed");
      }
    });
  });

  return (
    <>
      <PageHeader>
        <PageTitle title={t("missing_users.title")} />
      </PageHeader>
      <PageBody>
        <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
          <div className="flex-grow-1 d-flex align-items-center">
            <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
              <h3 className="text-secondary mb-0">
                {t("missing_users.list_title")}
              </h3>
            </Divider>
          </div>
          <div className="d-flex flex-row align-items-center justify-content-end">
            <Button
              type="default"
              className="btn-secondary m-2"
              onClick={() => downloadFile(missingUserExportUrl)}
            >
              <DownloadOutlined /> {t("missing_users.global_buttons.export")}
            </Button>
          </div>
        </div>
        <Table
          style={{ whiteSpace: "break-spaces" }}
          locale={{ emptyText: dataHasLoaded ? "" : " " }}
          loading={{ spinning: !dataHasLoaded, indicator: SpinIcon }}
          columns={columns}
          dataSource={missingUsers}
          showSorterTooltip={false}
          pagination={false}
        />
      </PageBody>
    </>
  );
};

export default MissingUserListsScreen;
